import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ShoppingCart, Package, Ruler, Scale, Sun, Truck, ScrollText } from 'lucide-react';
import { api } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const MarketplaceProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addItem, clearAndAdd } = useCart();

  const isSolarSkulls = product?.title?.toLowerCase().includes('solar skull');

  // Scroll to top when page loads or product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await api.getMarketplaceProduct(id);
        // API returns product with artist nested inside
        const { artist, ...productData } = data;
        setProduct(productData);
        setArtist(artist);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product && product.stock > 0) {
      // Add marketplace flag to distinguish from regular products
      const marketplaceProduct = { ...product, is_marketplace: true };
      const result = addItem(marketplaceProduct);
      
      if (result.blocked) {
        toast.error(
          `Your cart contains shop items. Please checkout or clear your cart first.`,
          {
            duration: 5000,
            action: {
              label: 'Clear Cart',
              onClick: () => {
                clearAndAdd(marketplaceProduct);
                toast.success(`Cart cleared. ${product.title} added!`);
              }
            }
          }
        );
        return;
      }
      
      toast.success(`${product.title} added to cart!`);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-void flex items-center justify-center">
        <div className="animate-pulse text-cyan-400 font-mono">Loading...</div>
      </div>
    );
  }

  if (!product || !artist) {
    return (
      <div className="pt-20 min-h-screen bg-void flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-400 mb-4">Product not found</p>
          <Link to="/marketplace" className="text-cyan-400 hover:text-cyan-300">
            Back to Marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="marketplace-product-page" className="pt-20 min-h-screen bg-void">
      {/* Back Button */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-white/5">
        <Link
          to="/marketplace"
          data-testid="back-to-marketplace"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm uppercase tracking-widest">Back to Community</span>
        </Link>
      </div>

      {/* Artist Calling Card Section - FIRST */}
      <div className="px-6 md:px-12 lg:px-24 py-12 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
              Meet the Artist
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
              {artist.name}
            </h2>
          </div>

          {/* Artist Card Image - Centered */}
          <div className="flex flex-col items-center">
            <img
              src={artist.card_image}
              alt={`${artist.name} - Artist Card`}
              data-testid="artist-card-image"
              className="max-w-md w-full rounded-lg shadow-2xl shadow-cyan-400/10 mb-8"
            />

            {/* View More Work Button */}
            {artist.website_url && (
              <a
                href={artist.website_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="artist-website-link"
                className="inline-flex items-center gap-2 bg-cyan-400 text-black px-8 py-4 font-mono text-sm uppercase tracking-widest hover:bg-cyan-300 transition-all hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
              >
                <ExternalLink className="w-4 h-4" />
                View More Work
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Product Section */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Images */}
            <div className="space-y-4 relative">
              {/* SAMPLE Stamp - only show if product is marked as sample */}
              {product.is_sample && (
                <div className="absolute top-4 left-4 z-20 -rotate-12">
                  <div className="bg-red-600 border-4 border-red-800 px-6 py-2 shadow-lg">
                    <span className="text-white font-mono text-lg font-bold tracking-widest uppercase">Sample</span>
                  </div>
                </div>
              )}
              
              <div className="aspect-square bg-void-paper border border-white/10 overflow-hidden">
                <img
                  src={product.images[selectedImage]?.url}
                  alt={product.images[selectedImage]?.alt || product.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-2">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 border overflow-hidden transition-all ${
                        selectedImage === index
                          ? 'border-cyan-400'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
                  Marketplace Item
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                  {product.title}
                </h1>
                <p className="text-3xl font-bold text-cyan-400 font-mono">
                  {product.price > 0 ? `$${product.price.toFixed(2)}` : 'FREE'}
                </p>
              </div>

              <p className="text-neutral-300 leading-relaxed">
                {product.description}
              </p>

              {/* Product Details */}
              <div className="space-y-3 py-4 border-t border-white/10">
                {product.dimensions && (
                  <div className="flex items-center gap-3 text-neutral-400">
                    <Ruler className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm"><span className="text-white">Dimensions:</span> {product.dimensions}</span>
                  </div>
                )}
                {product.materials && (
                  <div className="flex items-center gap-3 text-neutral-400">
                    <Package className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm"><span className="text-white">Materials:</span> {product.materials}</span>
                  </div>
                )}
                {product.weight && (
                  <div className="flex items-center gap-3 text-neutral-400">
                    <Scale className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm"><span className="text-white">Weight:</span> {product.weight}</span>
                  </div>
                )}
                {product.intended_use && (
                  <div className="flex items-center gap-3 text-neutral-400">
                    <Sun className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm"><span className="text-white">Intended Use:</span> {product.intended_use}</span>
                  </div>
                )}
                {/* Legal Notice - Only for Solar Skulls */}
                {isSolarSkulls && (
                  <div className="mt-4 p-4 bg-void border border-cyan-500/20 rounded">
                    <div className="flex items-start gap-3">
                      <ScrollText className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="text-cyan-400 font-semibold">Legal Notice:</span>
                        <p className="text-neutral-400 mt-1 leading-relaxed">
                          This product incorporates lawfully acquired, up-cycled materials that have been resold and modified in accordance with applicable laws, including the First Sale Doctrine. All customizations and modifications are independently performed by Only Skullz LLC and are not authorized, sponsored, or endorsed by the original manufacturer. No affiliation with the original brand is implied.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="text-neutral-400 space-y-2">
                  <div className="flex items-center gap-3">
                    <Truck className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm"><span className="text-white">Shipping:</span> Calculated at checkout</span>
                  </div>
                  <p className="text-xs text-neutral-500 pl-7">
                    Only Skullz LLC uses Shippo for all shipping to deliver fast, reliable orders at the best possible rates. It allows us to choose the best carrier every time and provide clear pricing and real-time tracking—so our clients get dependable delivery without the guesswork.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                {product.stock > 0 ? (
                  <span className="px-3 py-1 text-xs font-mono uppercase bg-green-500/20 text-green-400 border border-green-500/30">
                    {product.stock} in stock
                  </span>
                ) : (
                  <div className="flex items-center gap-3 bg-black/80 border border-red-500/50 px-4 py-2 rounded">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/xvcz8xtj_Skull%20Logo.jpeg" 
                      alt="Skull" 
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-red-500 font-mono text-sm uppercase font-bold tracking-wider">Sold Out</span>
                  </div>
                )}
              </div>

              {product.stock > 0 ? (
                <button
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-btn"
                  className="w-full bg-cyan-400 text-black px-8 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)] flex items-center justify-center gap-3"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </button>
              ) : (
                <button
                  disabled
                  className="w-full bg-neutral-700 text-neutral-400 px-8 py-4 font-mono uppercase tracking-widest text-sm cursor-not-allowed flex items-center justify-center gap-3"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Sold Out
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceProductPage;
